// src/features/monetize/components/MonetizePage.tsx
//
// Monetize (Revenue Suite): what the account sells. Invisible to accounts
// the api does not enable (useMonetizeEligibility is the gate); a direct URL
// hit while ineligible gets the not-available notice. The page re-applies the
// content access rules once per visit so a write that reached the catalog
// but not the rules heals itself.

import { useEffect, useRef } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Alert, Button, Group, Skeleton, Stack, Tabs, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { GatingSync } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { useMonetizeCatalog } from '../useMonetizeCatalog';
import { useMonetizeEligibility } from '../useMonetizeEligibility';
import { useSyncGating } from '../useMonetizeMutations';
import { useMonetizeStatus } from '../useMonetizeStatus';
import { OffersTab } from './OffersTab';
import { OverviewTab } from './OverviewTab';
import { ProductsTab } from './ProductsTab';
import { SettingsTab } from './SettingsTab';
import { TemplatesTab } from './TemplatesTab';
import { ViewersTab } from './ViewersTab';

const VALID_TABS = ['overview', 'products', 'offers', 'templates', 'viewers', 'settings'] as const;
type MonetizeTab = (typeof VALID_TABS)[number];

function parseTab(raw: unknown): MonetizeTab | null {
  return typeof raw === 'string' && (VALID_TABS as readonly string[]).includes(raw) ? (raw as MonetizeTab) : null;
}

export function MonetizePage() {
  const { eligible, isLoading: eligibilityLoading } = useMonetizeEligibility();
  const { status } = useMonetizeStatus();
  const { selectedCId } = useAuth();
  const hasProfile = !!status?.profile;
  const { catalog, isLoading: catalogLoading, isError, error } = useMonetizeCatalog();
  const sync = useSyncGating();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const tab: MonetizeTab = parseTab(search.tab) ?? (hasProfile ? 'overview' : 'settings');
  const syncedFor = useRef<number | null>(null);

  // Heal the access rules once per account visit (idempotent: a matching catalog writes nothing).
  useEffect(() => {
    if (!eligible || !hasProfile || selectedCId == null || syncedFor.current === selectedCId) return;
    syncedFor.current = selectedCId;
    sync.mutate(undefined, {
      onSuccess: (res) => reportSync(res.gatingSync),
      onError: () => undefined,
    });
  }, [eligible, hasProfile, selectedCId, sync]);

  function setTab(value: string | null) {
    const next = parseTab(value);
    if (next) navigate({ to: '/monetize', search: { tab: next }, replace: true });
  }

  if (eligibilityLoading) {
    return (
      <Stack gap="md">
        <Title order={2}>Monetize</Title>
        <Skeleton height={120} />
      </Stack>
    );
  }

  if (!eligible || !status) {
    return (
      <Stack gap="md">
        <Title order={2}>Monetize</Title>
        <Alert color="gray" icon={<IconInfoCircle size={16} />}>Monetization is not available for this account.</Alert>
      </Stack>
    );
  }

  const gating = catalog?.gating ?? [];

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Title order={2}>Monetize</Title>
          {hasProfile && (
            <Text size="sm" c="dimmed">{catalogLoading ? 'Loading your catalog…' : MonetizeUtils.gatingSummary(gating)}</Text>
          )}
        </Stack>
        {hasProfile && (
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={sync.isPending}
            onClick={() => sync.mutate(undefined, { onSuccess: (res) => reportSync(res.gatingSync, true), onError: (err) => notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not re-apply the access rules.'), color: 'red' }) })}
          >
            Re-apply access rules
          </Button>
        )}
      </Group>

      {isError && <Alert color="red" icon={<IconAlertTriangle size={16} />}>{error instanceof Error ? error.message : 'Failed to load the catalog.'}</Alert>}

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overview" disabled={!hasProfile}>Dashboard</Tabs.Tab>
          <Tabs.Tab value="products" disabled={!hasProfile}>Products</Tabs.Tab>
          <Tabs.Tab value="offers" disabled={!hasProfile}>Promo codes</Tabs.Tab>
          <Tabs.Tab value="templates" disabled={!hasProfile}>Templates</Tabs.Tab>
          <Tabs.Tab value="viewers" disabled={!hasProfile}>Viewers</Tabs.Tab>
          <Tabs.Tab value="settings">Settings</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="overview" pt="md">
          <OverviewTab />
        </Tabs.Panel>
        <Tabs.Panel value="products" pt="md">
          {catalogLoading ? <Skeleton height={160} /> : <ProductsTab products={catalog?.products ?? []} onGatingSync={reportSync} />}
        </Tabs.Panel>
        <Tabs.Panel value="offers" pt="md">
          {catalogLoading ? <Skeleton height={160} /> : <OffersTab offers={catalog?.offers ?? []} products={catalog?.products ?? []} />}
        </Tabs.Panel>
        <Tabs.Panel value="templates" pt="md">
          <TemplatesTab onApplied={reportSync} />
        </Tabs.Panel>
        <Tabs.Panel value="viewers" pt="md">
          <ViewersTab />
        </Tabs.Panel>
        <Tabs.Panel value="settings" pt="md">
          <SettingsTab status={status} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

/** A failed reconciliation is the one thing worth an orange toast: the catalog saved, the rules did not. */
function reportSync(sync: GatingSync | undefined, announceOk = false) {
  if (!sync) return;
  if (!sync.ok) {
    notifications.show({ title: 'Access rules not applied', message: sync.message ?? 'Use Re-apply access rules.', color: 'orange', autoClose: 8000 });
  } else if (announceOk) {
    notifications.show({ title: 'Access rules applied', message: `${sync.upserts ?? 0} updated, ${sync.deletes ?? 0} removed.`, color: 'green' });
  }
}
